/**
 * @class
 * @function StackedMediaTabs
 */
if (!customElements.get('stacked-media-tabs')) {
	class StackedMediaTabs extends HTMLElement {
		constructor() {
			super();
		}

		connectedCallback() {
			this.tabs = Array.from(this.querySelectorAll('.stacked-media-tabs--tab'));
			this.tabsWrapper = this.querySelector('.stacked-media-tabs--tabs');
			this.slides = Array.from(this.querySelectorAll('.stacked-media-tabs--media-slide'));
			this.mobileDescription = this.querySelector('.stacked-media-tabs--mobile-description');
			this.handleResize = this.handleWindowResize.bind(this);

			this.tabs.forEach((tab) => {
				tab.addEventListener('click', this.handleTabClick.bind(this));
			});

			this.updateMedia();
			this.updateDescriptions();
			this.updateMobileDescriptionHeight();
			this.centerActiveTab(false);
			window.addEventListener('resize', this.handleResize);
		}

		disconnectedCallback() {
			window.removeEventListener('resize', this.handleResize);
		}

		handleTabClick(event) {
			const tab = event.currentTarget;
			const index = tab.dataset.index;

			this.tabs.forEach((t) => {
				t.classList.remove('active');
				t.setAttribute('aria-selected', 'false');
			});

			tab.classList.add('active');
			tab.setAttribute('aria-selected', 'true');

			this.updateMedia(index);
			this.updateDescriptions();
			this.centerActiveTab(true);
		}

		updateMedia(activeIndex) {
			if (activeIndex === undefined || activeIndex === null) {
				const activeTab = this.tabs.find((t) => t.classList.contains('active'));
				activeIndex = activeTab ? activeTab.dataset.index : (this.tabs[0] ? this.tabs[0].dataset.index : null);
			}
			if (activeIndex === null) return;
			this.slides.forEach((slide) => {
				const isActive = slide.dataset.index === activeIndex;
				slide.classList.toggle('active', isActive);
				const video = slide.querySelector('video');
				if (video) {
					if (isActive) {
						video.play().catch(() => {});
					} else {
						video.pause();
					}
				}
			});
		}

		updateDescriptions() {
			const activeTab = this.tabs.find((t) => t.classList.contains('active'));
			if (!activeTab) return;
			const description = activeTab.dataset.description || '';
			if (this.mobileDescription) {
				this.mobileDescription.textContent = description;
			}
			this.updateMobileDescriptionHeight();
		}

		updateMobileDescriptionHeight() {
			if (!this.mobileDescription || !this.tabsWrapper) return;

			const isDesktop = window.matchMedia('(min-width: 768px)').matches;
			let width = this.mobileDescription.clientWidth;

			if (isDesktop) {
				const tabWidth = this.tabsWrapper.scrollWidth;
				this.mobileDescription.style.maxWidth = tabWidth ? `${tabWidth}px` : '';
				width = Math.min(width, tabWidth || width);
			}

			if (!width) return;

			const measurer = document.createElement('div');
			measurer.className = 'stacked-media-tabs--mobile-description stacked-media-tabs--mobile-description--measure';
			measurer.style.width = `${width}px`;
			this.appendChild(measurer);

			let maxHeight = 0;
			this.tabs.forEach((tab) => {
				measurer.textContent = tab.dataset.description || '';
				maxHeight = Math.max(maxHeight, measurer.scrollHeight);
			});

			measurer.remove();
			this.mobileDescription.style.minHeight = `${maxHeight}px`;
		}

		handleWindowResize() {
			this.updateMobileDescriptionHeight();
			this.centerActiveTab(false);
		}

		centerActiveTab(smooth) {
			if (!this.tabsWrapper) return;

			const activeTab = this.tabs.find((t) => t.classList.contains('active'));
			if (!activeTab) return;
			const activeIndex = this.tabs.indexOf(activeTab);

			const wrapperRect = this.tabsWrapper.getBoundingClientRect();
			const tabRect = activeTab.getBoundingClientRect();
			let targetLeft = this.tabsWrapper.scrollLeft
				+ (tabRect.left - wrapperRect.left)
				- (wrapperRect.width / 2)
				+ (tabRect.width / 2);
			const maxScrollLeft = this.tabsWrapper.scrollWidth - this.tabsWrapper.clientWidth;

			// first tab keeps left spacing, middle tabs center, last tab keeps right spacing.
			if (activeIndex === 0) {
				targetLeft = 0;
			} else if (activeIndex === this.tabs.length - 1) {
				targetLeft = maxScrollLeft;
			}

			const clampedLeft = Math.max(0, Math.min(targetLeft, maxScrollLeft));

			this.tabsWrapper.scrollTo({
				left: clampedLeft,
				behavior: smooth ? 'smooth' : 'auto'
			});
		}
	}

	customElements.define('stacked-media-tabs', StackedMediaTabs);
}
